///
/// EarthBrowser
///
/// Created by Matt Giger on September 13, 2026
/// Copyright (c) 2026 Geodata Labs
///

/// Storage Manager tab for OPFS-backed SQLite VFS volumes and GeoPackage files.

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChevronDown, ChevronRight, FileArchive, HardDrive, Trash2 } from 'lucide-react'
import { useState } from 'react'
import type { StorageTabProps } from '@/components/features/dev-tools/storage-manager/StorageManager'
import {
	formatBytes,
	formatFlags,
	formatVolumeName,
	volumeSortOrder,
	type VFSFileInfo,
	type VFSVolumeInfo,
} from '@/components/features/dev-tools/storage-manager/storageScanner'
import {
	deleteGeoPackageFile,
	deleteVFSFile,
	deleteVFSVolume,
} from '@/components/features/dev-tools/storage-manager/storageActions'

function VolumeCard({ volume, busy, runAction }: { volume: VFSVolumeInfo } & Pick<StorageTabProps, 'busy' | 'runAction'>) {
	const [showUnassociated, setShowUnassociated] = useState(false)

	const pathGroups = new Map<string, VFSFileInfo[]>()
	volume.files.forEach(file => {
		const existing = pathGroups.get(file.path) || []
		existing.push(file)
		pathGroups.set(file.path, existing)
	})
	const unassociated = pathGroups.get('(unassociated)') || []

	const onDeleteVolume = () => {
		if (!confirm(`Delete entire volume "${formatVolumeName(volume.name)}" (${volume.name})? This cannot be undone.\n\nIf the volume is locked, reload the page first to release VFS locks.`)) {
			return
		}
		void runAction(() => deleteVFSVolume(volume.name))
	}

	const onDeleteFile = (fileName: string) => {
		if (!confirm(`Delete VFS file "${fileName}"? This cannot be undone.`)) {
			return
		}
		void runAction(() => deleteVFSFile(volume.name, fileName))
	}

	const renderFile = (file: VFSFileInfo) => (
		<div key={file.name} className="flex items-center justify-between p-2 bg-secondary/50 rounded text-xs">
			<div className="space-y-1 flex-1 min-w-0">
				<div className="font-mono truncate">{file.name}</div>
				<div className="text-muted-foreground">
					{formatBytes(file.size)} total, {formatBytes(file.dataSize)} data • {formatFlags(file.flags)}
				</div>
			</div>
			<Button variant="ghost" size="sm" onClick={() => onDeleteFile(file.name)} disabled={busy}>
				<Trash2 className="h-3 w-3" />
			</Button>
		</div>
	)

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center justify-between text-base">
					<div className="flex items-center gap-2">
						<HardDrive className="h-4 w-4 text-blue-400" />
						{formatVolumeName(volume.name)}
					</div>
					<div className="flex items-center gap-2">
						<Badge variant="outline">{formatBytes(volume.totalSize)}</Badge>
						<Badge variant="outline">{volume.activePaths} active</Badge>
						<Badge variant="outline">{volume.files.length} files</Badge>
						<Button variant="destructive" size="sm" onClick={onDeleteVolume} disabled={busy} title="Delete this volume">
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>
				</CardTitle>
				<CardDescription className="font-mono text-xs">{volume.name}</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{Array.from(pathGroups.entries())
					.filter(([path]) => path !== '(unassociated)')
					.map(([path, files]) => (
						<div key={path} className="space-y-1">
							<div className="text-sm font-medium break-all">{path}</div>
							<div className="space-y-1 pl-4 border-l-2 border-slate-700">
								{files.map(renderFile)}
							</div>
						</div>
					))}

				{unassociated.length > 0 && (
					<div className="space-y-1">
						<button
							onClick={() => setShowUnassociated(prev => !prev)}
							className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
						>
							{showUnassociated ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
							Available file handles ({unassociated.length})
						</button>
						<div className="text-xs text-muted-foreground pl-5">
							Pre-allocated pool files not currently assigned to a database path
						</div>
						{showUnassociated && (
							<div className="space-y-1 pl-4 border-l-2 border-slate-700">
								{unassociated.map(renderFile)}
							</div>
						)}
					</div>
				)}

				{volume.files.length === 0 && (
					<div className="text-sm text-muted-foreground">Empty volume</div>
				)}
			</CardContent>
		</Card>
	)
}

export function VolumesTab({ snapshot, busy, runAction }: StorageTabProps) {
	const sortedVolumes = [...snapshot.volumes].sort(
		(a, b) => volumeSortOrder(a.name) - volumeSortOrder(b.name) || a.name.localeCompare(b.name)
	)

	const onDeleteGeoPackage = (fileName: string) => {
		if (!confirm(`Delete GeoPackage "${fileName}"? This cannot be undone.`)) {
			return
		}
		void runAction(() => deleteGeoPackageFile(fileName))
	}

	return (
		<div className="space-y-4">
			<div className="text-xs text-muted-foreground">
				SQLite databases are stored in OPFS volume directories. While the app is running,
				the VFS worker holds locks on all volume files — if a delete fails, reload the
				page first to release the locks.
			</div>

			{sortedVolumes.map(volume => (
				<VolumeCard key={volume.name} volume={volume} busy={busy} runAction={runAction} />
			))}

			{sortedVolumes.length === 0 && (
				<Card>
					<CardContent className="py-6 text-center text-sm text-muted-foreground">
						No VFS volumes found
					</CardContent>
				</Card>
			)}

			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-base">
						<FileArchive className="h-4 w-4 text-teal-400" />
						GeoPackages
					</CardTitle>
					<CardDescription>Source .gpkg files stored in OPFS</CardDescription>
				</CardHeader>
				<CardContent>
					{snapshot.geopackages.length === 0 ? (
						<div className="text-sm text-muted-foreground">No files found</div>
					) : (
						<div className="space-y-2">
							{snapshot.geopackages.map(file => (
								<div key={file.name} className="flex items-center justify-between p-2 border border-slate-700 rounded">
									<div className="space-y-1">
										<div className="text-sm font-medium">{file.name}</div>
										<div className="text-xs text-muted-foreground">{formatBytes(file.size)}</div>
									</div>
									<Button variant="ghost" size="sm" onClick={() => onDeleteGeoPackage(file.name)} disabled={busy}>
										<Trash2 className="h-3 w-3" />
									</Button>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
